// 받아쓰기(음성 → 텍스트) — 전체 화면 오버레이 패널.
// 파일 첨부/드래그 → 다글로 STT(타임스탬프 + 화자분리) → 결과 보기·다운로드·삭제.
// 목록/상세/삭제는 모두 본인 것만 보인다(서버에서 owner_email 로 격리).
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import { api, ForbiddenError, UnauthorizedError } from "@/api/client";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/cn";
import type { DictationDetail, DictationItem, DictationSegment } from "@/api/types";
import {
  AlertTriangle,
  Check,
  Copy,
  Download,
  FileAudio,
  Loader2,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";

interface Props {
  open: boolean;
  onClose: () => void;
}

// input[type=file] 의 accept — 서버(daglo.SUPPORTED_EXTENSIONS)와 같은 목록을 유지할 것.
const ACCEPT =
  ".mp3,.m4a,.wav,.flac,.ogg,.opus,.aac,.wma,.amr,.mp4,.mov,.avi,.mkv,.webm,audio/*,video/*";

// 진행 중인 항목이 있을 때만 목록을 다시 부른다(폴링 간격).
const POLL_MS = 5000;

function fmtDuration(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const r = s % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(r)}` : `${m}:${pad(r)}`;
}

function fmtSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  return `${Math.max(1, Math.round(bytes / 1024))}KB`;
}

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")} ${String(d.getHours()).padStart(2, "0")}:${String(
    d.getMinutes(),
  ).padStart(2, "0")}`;
}

/** 화자 번호별 색 — 최대 8명까지 구분되고, 그 이상은 순환한다. */
const SPEAKER_CLASSES = [
  "bg-sky-100 text-sky-800",
  "bg-emerald-100 text-emerald-800",
  "bg-amber-100 text-amber-800",
  "bg-violet-100 text-violet-800",
  "bg-rose-100 text-rose-800",
  "bg-teal-100 text-teal-800",
  "bg-indigo-100 text-indigo-800",
  "bg-lime-100 text-lime-800",
];
function speakerClass(speaker: string): string {
  const n = Number.parseInt(speaker, 10);
  const idx = Number.isFinite(n) ? Math.abs(n - 1) : 0;
  return SPEAKER_CLASSES[idx % SPEAKER_CLASSES.length];
}

function hms(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}`;
}

function StatusBadge({ item }: { item: DictationItem }) {
  if (item.status === "done") {
    return (
      <span className="flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800">
        <Check className="h-3 w-3" /> 변환 완료
      </span>
    );
  }
  if (item.status === "failed") {
    return (
      <span className="flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-semibold text-rose-800">
        <AlertTriangle className="h-3 w-3" /> 실패
      </span>
    );
  }
  return (
    <span className="flex items-center gap-1 rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-800">
      <Loader2 className="h-3 w-3 animate-spin" />
      {item.status === "uploaded" ? "변환 대기" : "변환 중"}
    </span>
  );
}

export default function DictationPanel({ open, onClose }: Props) {
  const [items, setItems] = useState<DictationItem[]>([]);
  const [enabled, setEnabled] = useState(true);
  const [maxMb, setMaxMb] = useState(50);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState<{ name: string; pct: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [detail, setDetail] = useState<DictationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<DictationItem | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      const r = await api.dictations();
      setItems(r.items);
      setEnabled(r.enabled);
      setMaxMb(r.max_mb);
    } catch (e) {
      if (e instanceof UnauthorizedError) return; // 중앙에서 로그인 화면 전환
      if (e instanceof ForbiddenError) toast(e.message, "error");
      else toast("받아쓰기 목록을 불러오지 못했습니다.", "error");
    }
  }, []);

  // 열릴 때 1회 조회
  useEffect(() => {
    if (!open) return;
    setLoading(true);
    reload().finally(() => setLoading(false));
  }, [open, reload]);

  // 진행 중(uploaded/transcribing) 항목이 있는 동안만 폴링 — 완료되면 저절로 멈춘다.
  const pending = items.some((i) => i.status === "uploaded" || i.status === "transcribing");
  useEffect(() => {
    if (!open || !pending) return;
    const t = setInterval(reload, POLL_MS);
    return () => clearInterval(t);
  }, [open, pending, reload]);

  // ESC — 열린 모달부터 차례로 닫는다
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      if (deleteTarget) setDeleteTarget(null);
      else if (detail) setDetail(null);
      else onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, detail, deleteTarget, onClose]);

  const upload = useCallback(
    async (file: File) => {
      if (!enabled) {
        toast("받아쓰기가 아직 설정되지 않았습니다. 관리자에게 문의해 주세요.", "error");
        return;
      }
      if (uploading) {
        toast("이미 올리는 중입니다. 끝나면 다음 파일을 올려 주세요.", "info");
        return;
      }
      // 서버도 같은 한도로 막지만, 큰 파일을 다 올린 뒤 거절당하지 않도록 미리 확인한다.
      if (file.size > maxMb * 1024 * 1024) {
        toast(
          `파일이 너무 큽니다(${fmtSize(file.size)}). 최대 ${maxMb}MB 까지 올릴 수 있습니다.`,
          "error",
        );
        return;
      }
      setUploading({ name: file.name, pct: 0 });
      try {
        const created = await api.dictationUpload(file, (pct) =>
          setUploading({ name: file.name, pct }),
        );
        setItems((prev) => [created, ...prev]);
        toast("음성을 올렸습니다. 변환이 끝나면 목록에 표시됩니다.", "success");
      } catch (e) {
        if (!(e instanceof UnauthorizedError)) {
          toast(e instanceof Error ? e.message : "업로드에 실패했습니다.", "error");
        }
      } finally {
        setUploading(null);
      }
    },
    [enabled, maxMb, uploading],
  );

  const pickFiles = (files: FileList | null) => {
    const list = Array.from(files ?? []);
    if (list.length === 0) return;
    if (list.length > 1) toast("한 번에 한 개씩 변환합니다. 첫 번째 파일만 올립니다.", "info");
    upload(list[0]);
  };

  const onDragOver = (e: DragEvent) => {
    if (!e.dataTransfer?.types?.includes("Files")) return;
    e.preventDefault();
    setDragOver(true);
  };
  const onDragLeave = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
  };
  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    pickFiles(e.dataTransfer?.files ?? null);
  };

  const openDetail = async (item: DictationItem) => {
    if (item.status !== "done") return;
    setDetailLoading(true);
    try {
      setDetail(await api.dictationDetail(item.id));
    } catch (e) {
      if (!(e instanceof UnauthorizedError)) toast("전사 내용을 불러오지 못했습니다.", "error");
    } finally {
      setDetailLoading(false);
    }
  };

  const doDelete = async (item: DictationItem) => {
    try {
      await api.dictationDelete(item.id);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (detail?.id === item.id) setDetail(null);
      setDeleteTarget(null);
      toast("삭제했습니다.", "success");
    } catch (e) {
      if (!(e instanceof UnauthorizedError)) toast("삭제에 실패했습니다.", "error");
    }
  };

  const copyTranscript = async (d: DictationDetail) => {
    try {
      await navigator.clipboard.writeText(d.transcript || "");
      toast("전사 내용을 복사했습니다.", "success");
    } catch {
      toast("복사에 실패했습니다. 다운로드를 이용해 주세요.", "error");
    }
  };

  if (!open) return null;

  const dlBtn =
    "flex items-center gap-1 rounded-lg border border-border px-2.5 py-1.5 text-xs font-semibold hover:bg-muted";

  return (
    <div
      className="fixed inset-0 z-40 flex flex-col bg-background"
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      {/* 헤더 — pt 는 safe-area 와 기본 패딩 중 큰 값(데스크톱에서 env() 가 0이라 붙는 문제 방지) */}
      <div className="flex items-center gap-2 border-b border-border px-3 py-2.5 pt-[max(env(safe-area-inset-top),0.625rem)]">
        <span className="font-bold">받아쓰기</span>
        <span className="hidden text-xs text-muted-foreground sm:inline">
          음성 파일을 올리면 타임스탬프와 화자를 구분해 텍스트로 바꿔 드립니다
        </span>
        <div className="flex-1" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={!enabled || !!uploading}
          className="flex items-center gap-1 rounded-lg bg-[#0E1E2D] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#1C3954] disabled:opacity-40"
        >
          <UploadCloud className="h-4 w-4" /> 음성 올리기
        </button>
        <button onClick={onClose} aria-label="닫기" className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted">
          <X className="h-5 w-5" />
        </button>
      </div>

      <input
        ref={fileRef}
        type="file"
        accept={ACCEPT}
        className="hidden"
        onChange={(e) => {
          pickFiles(e.target.files);
          e.target.value = ""; // 같은 파일을 연속으로 다시 고를 수 있게 초기화
        }}
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-3 py-3">
        <div className="mx-auto max-w-3xl space-y-3">
          {!enabled && (
            <div className="flex items-start gap-2 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <span>
                받아쓰기가 아직 설정되지 않았습니다(다글로 API 토큰 미등록). 관리자에게 문의해
                주세요.
              </span>
            </div>
          )}

          {/* 드롭존 — 클릭해서 고를 수도 있고, 화면 어디에 떨어뜨려도 받는다 */}
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={!enabled || !!uploading}
            className={cn(
              "flex w-full flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-dashed px-4 py-7 text-center transition-colors",
              dragOver
                ? "border-[#0E1E2D] bg-[#0E1E2D]/5"
                : "border-border hover:border-foreground/30 hover:bg-muted/50",
              (!enabled || !!uploading) && "opacity-50",
            )}
          >
            <UploadCloud className="h-7 w-7 text-muted-foreground" />
            <span className="text-sm font-semibold">
              여기로 음성 파일을 끌어다 놓거나, 눌러서 선택하세요
            </span>
            <span className="text-xs text-muted-foreground">
              mp3 · m4a · wav · flac · mp4 등 · 최대 {maxMb}MB
            </span>
          </button>

          {/* 업로드 진행률 */}
          {uploading && (
            <div className="rounded-xl border border-border p-3">
              <div className="mb-1.5 flex items-center gap-2 text-sm">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{uploading.name}</span>
                <span className="tabular-nums text-xs text-muted-foreground">
                  {uploading.pct}%
                </span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-muted">
                <div
                  className="h-full rounded-full bg-[#0E1E2D] transition-[width]"
                  style={{ width: `${uploading.pct}%` }}
                />
              </div>
            </div>
          )}

          {/* 목록 */}
          {loading && items.length === 0 ? (
            <div className="flex justify-center py-10 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              아직 변환한 음성이 없습니다.
            </p>
          ) : (
            <ul className="space-y-2">
              {items.map((it) => {
                const ready = it.status === "done";
                return (
                  <li
                    key={it.id}
                    className={cn(
                      "rounded-xl border border-border p-3",
                      ready && "cursor-pointer hover:bg-muted/50",
                    )}
                    onClick={() => ready && openDetail(it)}
                  >
                    <div className="flex items-start gap-2">
                      <FileAudio className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-semibold">{it.filename}</p>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {fmtWhen(it.created_at)} · {fmtSize(it.size_bytes)}
                          {ready && (
                            <>
                              {" · "}
                              {fmtDuration(it.duration_sec)} · 화자 {it.speaker_count}명 · 발언{" "}
                              {it.segment_count}개
                            </>
                          )}
                        </p>
                        {it.status === "failed" && it.error && (
                          <p className="mt-1 text-xs text-rose-700">{it.error}</p>
                        )}
                      </div>
                      <div className="flex shrink-0 flex-col items-end gap-1.5">
                        <StatusBadge item={it} />
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(it);
                          }}
                          aria-label="삭제"
                          title="삭제"
                          className="rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-destructive"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                    {ready && (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button onClick={() => openDetail(it)} className={dlBtn}>
                          내용 보기
                        </button>
                        <a href={api.dictationDownloadUrl(it.id, "txt")} className={dlBtn} download>
                          <Download className="h-3.5 w-3.5" /> 텍스트
                        </a>
                        <a href={api.dictationDownloadUrl(it.id, "srt")} className={dlBtn} download>
                          <Download className="h-3.5 w-3.5" /> SRT
                        </a>
                        <a href={api.dictationDownloadUrl(it.id, "vtt")} className={dlBtn} download>
                          <Download className="h-3.5 w-3.5" /> VTT
                        </a>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* 드래그 오버레이 — 패널 어디에 떨어뜨려도 받는다는 걸 보여준다 */}
      {dragOver && (
        <div className="pointer-events-none absolute inset-0 z-[60] flex items-center justify-center bg-[#0E1E2D]/10">
          <div className="rounded-xl bg-background px-5 py-3 text-sm font-semibold shadow-lg">
            여기에 놓으면 받아쓰기를 시작합니다
          </div>
        </div>
      )}

      {detailLoading && (
        <div className="absolute inset-0 z-[55] flex items-center justify-center bg-background/60">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      )}

      {/* 전사 내용 — 타임스탬프 + 화자별 블록 */}
      {detail && (
        <div
          className="fixed inset-0 z-[65] flex items-center justify-center bg-black/40 p-3"
          onClick={() => setDetail(null)}
        >
          <div
            className="flex max-h-[90vh] w-full max-w-2xl flex-col rounded-xl bg-card shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start gap-2 border-b border-border p-3">
              <div className="min-w-0 flex-1">
                <h3 className="truncate text-base font-bold">{detail.filename}</h3>
                <p className="mt-0.5 text-xs text-muted-foreground">
                  {fmtDuration(detail.duration_sec)} · 화자 {detail.speaker_count}명 · 발언{" "}
                  {detail.segment_count}개
                </p>
              </div>
              <button
                onClick={() => setDetail(null)}
                aria-label="닫기"
                className="rounded-md p-1 text-muted-foreground hover:bg-muted"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="min-h-0 flex-1 space-y-2.5 overflow-y-auto p-3">
              {detail.segments.map((s: DictationSegment, i: number) => (
                <div key={i}>
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <span className="tabular-nums text-[11px] text-muted-foreground">
                      {hms(s.start)}
                    </span>
                    <span
                      className={cn(
                        "rounded-full px-2 py-0.5 text-[11px] font-semibold",
                        speakerClass(s.speaker),
                      )}
                    >
                      화자 {s.speaker}
                    </span>
                  </div>
                  <p className="whitespace-pre-wrap text-sm leading-relaxed">{s.text}</p>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap justify-end gap-1.5 border-t border-border p-3">
              <button onClick={() => copyTranscript(detail)} className={dlBtn}>
                <Copy className="h-3.5 w-3.5" /> 복사
              </button>
              <a href={api.dictationDownloadUrl(detail.id, "txt")} className={dlBtn} download>
                <Download className="h-3.5 w-3.5" /> 텍스트
              </a>
              <a href={api.dictationDownloadUrl(detail.id, "srt")} className={dlBtn} download>
                <Download className="h-3.5 w-3.5" /> SRT
              </a>
              <a href={api.dictationDownloadUrl(detail.id, "vtt")} className={dlBtn} download>
                <Download className="h-3.5 w-3.5" /> VTT
              </a>
              <button
                onClick={() => setDetail(null)}
                className="rounded-lg bg-[#0E1E2D] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[#1C3954]"
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 삭제 확인 */}
      {deleteTarget && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-black/40 p-4"
          onClick={() => setDeleteTarget(null)}
        >
          <div
            className="w-full max-w-sm rounded-xl bg-card p-4 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-1 flex items-center gap-1.5 font-semibold">
              <Trash2 className="h-4 w-4 text-muted-foreground" /> 받아쓰기 삭제
            </div>
            <p className="mb-1 truncate text-sm">"{deleteTarget.filename}"</p>
            <p className="mb-3 text-xs text-muted-foreground">
              변환한 텍스트와 올린 음성이 모두 지워집니다. 되돌릴 수 없습니다.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setDeleteTarget(null)}
                className="rounded-lg px-3 py-2 text-sm text-muted-foreground hover:bg-muted"
              >
                취소
              </button>
              <button
                onClick={() => doDelete(deleteTarget)}
                className="rounded-lg border border-destructive px-3 py-2 text-sm font-semibold text-destructive hover:bg-destructive/10"
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
